import { AfterViewInit, Component, OnInit, ViewChild, ViewEncapsulation } from "@angular/core";
import { ChangeElement, ChangeService } from "../shared/services/change.service";
import { Subject, takeUntil } from "rxjs";
import { HcSort, HcTableDataSource, PaginationComponent } from "@healthcatalyst/cashmere";
import { TypeService } from "../shared/services/type.service";
import { ProductLineService } from "../shared/services/product-line.service";
import { StatusService } from "../shared/services/status.service";
import { SLAService } from "../shared/services/sla.service";
import { ActivatedRoute } from "@angular/router";
import { DownloadElement, DownloadService } from "../shared/services/download.service";

/** Admin change log */
@Component({
    selector: 'pd-changes',
    templateUrl: './changes.component.html',
    styleUrls: ['./changes.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class ChangesComponent implements OnInit, AfterViewInit {
    private unsubscribe = new Subject<void>();
    libraryID = '';
    dataSource: HcTableDataSource<ChangeElement> = new HcTableDataSource();
    manyFields = ['Success Stories', 'Images', 'Included Technology', 'Associated Services', 'Previous Names'];
    htmlFields = ['Tagline', 'Problem', 'Approach', 'Benefits and Features', 'Technical Description'];
    idFields = ['Type', 'Product Line', 'Status', 'SLA'];
    displayedColumns: string[] = ['date', 'item-id', 'name', 'user', 'field', 'previous', 'change'];
    pageSize: number = 10;
    loading = true;
    loadError = false;
    typeKey: { [key: string]: string } = {};
    productLineKey: { [key: string]: string } = {};
    statusKey: { [key: string]: string } = {};
    slaKey: { [key: string]: string } = {};

    @ViewChild(HcSort)
    sort: HcSort;

    @ViewChild(PaginationComponent)
    paginator: PaginationComponent;

    get listLength(): number {
        return this.dataSource.filteredData ? this.dataSource.filteredData.length : 0;
    }

    constructor(
        private changeService: ChangeService,
        private typeService: TypeService,
        private productLineService: ProductLineService,
        private statusService: StatusService,
        private slaService: SLAService,
        private activatedRoute: ActivatedRoute,
        private downloadService: DownloadService
    ) {}

    ngOnInit(): void {
        this.loading = true;
        this.activatedRoute.queryParams.pipe(takeUntil(this.unsubscribe)).subscribe(params => {
            if (params['id']) {
                this.libraryID = decodeURI(params['id']);
            }
        });

        this.typeService.allTypeKeyUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.typeKey = list;
                if (this.productLineKey && this.statusKey && this.slaKey) {
                    this.loadChanges();
                }
            } else {
                this.typeService.loadAllTypes();
            }
        });

        this.productLineService.productLineKeyUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.productLineKey = list;
                if (this.typeKey && this.statusKey && this.slaKey) {
                    this.loadChanges();
                }
            } else {
                this.productLineService.refresh();
            }
        });

        this.statusService.statusKeyUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.statusKey = list;
                if (this.typeKey && this.productLineKey && this.slaKey) {
                    this.loadChanges();
                }
            } else {
                this.statusService.refresh();
            }
        });

        this.slaService.slaKeyUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.slaKey = list;
                if (this.typeKey && this.productLineKey && this.statusKey) {
                    this.loadChanges();
                }
            } else {
                this.slaService.refresh();
            }
        });
    }

    ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
    }

    formatID(id: number, type: string) {
        let reformattedID: string = '';

        if (type === "Type") {
            reformattedID = this.typeKey[id.toString()];
        } else if (type === "Product Line") {
            reformattedID = this.productLineKey[id.toString()];
        } else if (type === 'Status') {
            reformattedID = this.statusKey[id.toString()];
        } else if (type === 'SLA') {
            reformattedID = this.slaKey[id.toString()];
        }

        return reformattedID
    }

    //create and object from a string
    createObj(obj: any) {
        return JSON.parse(obj);
    }

    loadChanges() {
        this.downloadService.downloadsUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                let download: DownloadElement | null = null;
                for (let index = 0; index < list.length; index++) {
                    if ( list[index].name === this.libraryID ) {
                        download = list[index];
                        break;
                    }
                }

                if ( download ) {
                    this.changeService
                        .load({
                            categories: "technology,service,partner,suite",
                            startDate: download.startDate,
                            endDate: download.endDate
                        })
                        .pipe(takeUntil(this.unsubscribe))
                        .subscribe({
                            next: data => {
                                let loadedChanges: ChangeElement[] = [];
                                //display changes with most recent change first
                                data.reverse().forEach((change: any) => {
                                    loadedChanges.push(change);
                                });
                                this.dataSource = new HcTableDataSource(loadedChanges);
                                this.dataSource.sort = this.sort;
                                this.dataSource.paginator = this.paginator;

                                this.loadError = false;
                                this.loading = false;
                            },
                            error: msg => {
                                this.loadError = true;
                                this.loading = false;
                                console.log(msg);
                            }
                        });
                } else {
                    this.loadError = true;
                    this.loading = false;
                }
            } else {
                this.loadError = this.downloadService.loadFailed;
                if ( !this.loadError ) {
                    this.downloadService.refresh();
                } else {
                    this.loading = false;
                }
            }
        });
    }
}